<template>

  <div class="desktop-video-player-container"
       :ref="containerRef"
       @fullscreenchange="escapeFullscreen"
       @webkitfullscreenchange="escapeFullscreen"
       @mozfullscreenchange="escapeFullscreen"
       @MSfullscreenchange="escapeFullscreen">

    <video class="desktop-video-player"
           :ref="playerRef"
           playsinline
           tabindex="0"
           width="100%"
           :style="playerStyle"
           @durationchange="durationChange"
           @waiting="startBuffering"
           @ended="videoEnded"></video>

    <div class="desktop-video-player-poster" :ref="posterRef" v-show="posterDisplayed" @click="posterClicked" @mouseover="posterMouseover" @mouseout="posterMouseout">
      <div class="desktop-club-names-container" v-if="embed">
        <a class="desktop-club-names" :class="'large'" :href="embedToRematchLink" target="_blank">{{ video.club1Alias.toUpperCase() }} - {{ video.club2Alias.toUpperCase() }}</a>
      </div>
      <img class="big-play-button" :class="'large'" alt="Lancer la vidéo" v-if="!posterHovered" src="../../static/icons/videoplayer/big-play-button-256x256-white.png"/>
      <img class="big-play-button" :class="'large'" alt="Lancer la vidéo" v-if="posterHovered" src="../../static/icons/videoplayer/big-play-button-256x256-blue.png"/>
    </div>

    <div class="desktop-skeleton-container" v-if="skeletonDisplayed">
      <div class="desktop-skeleton-loader" :class="'large'">
        <p>loading</p>
      </div>
    </div>

    <div class="desktop-anim-container" v-show="playAnimToggled || pauseAnimToggled">
      <div class="desktop-anim-content" v-show="playAnimToggled">
        <transition name="playanim">
          <img class="desktop-play-anim" v-show="playAnimToggled" alt="Vidéo lancée" src="../../static/icons/videoplayer/play-anim-256x256-white.png"/>
        </transition>
      </div>
      <div class="desktop-anim-content" v-show="pauseAnimToggled">
        <transition name="pauseanim">
          <img class="desktop-pause-anim" v-show="pauseAnimToggled" alt="Vidéo en pause" src="../../static/icons/videoplayer/pause-anim-256x256-white.png"/>
        </transition>
      </div>
    </div>

    <div class="desktop-buffering-loader-container" v-if="loading && hitTriggered">
      <div class="desktop-buffering-loader">
        <p>loading</p>
      </div>
    </div>

    <transition name="endedoverlay">
      <div class="desktop-ended-overlay" v-show="ended">
        <v-btn class="desktop-ended-replay-button" text outline small color="primary" @click="playVideo">REPLAY</v-btn>
      </div>
    </transition>

    <div class="desktop-video-player-controls"
         v-show="!posterDisplayed"
         :class="controlsDisplayed ? 'visible' : 'hidden'"
         @click.stop="playerClicked"
         @dblclick.stop="fullscreenClicked"
         @mouseover="controlsMouseover"
         @mouseout="controlsMouseout">

      <div class="desktop-club-names-container in-player" v-if="embed">
        <a class="desktop-club-names" :class="'large'" :href="embedToRematchLink" target="_blank">{{ video.club1Alias.toUpperCase() }} - {{ video.club2Alias.toUpperCase() }}</a>
      </div>

      <div class="desktop-controls-bar">

        <div class="desktop-seek-bar"
             :ref="seekBarRef"
             @mousedown.stop="seekBarMouseDown"
             @click.stop="''"
             @dblclick.stop="''">
          <div class="desktop-seek-bar-pending"></div>
          <div class="desktop-seek-bar-buffered" :style="bufferedSeekBarStyle"></div>
          <div class="desktop-seek-bar-read" :style="readSeekBarStyle"></div>
          <div class="desktop-seek-bar-tooltip" v-show="seekBarClicked" :style="tooltipStyle">
            <p class="desktop-seek-bar-tooltip-txt">{{ formatMinutes(Math.floor(seekTime)) }}:{{ formatSeconds(Math.floor(seekTime)) }}</p>
          </div>
          <div class="desktop-seek-bar-handle-container"
               :style="seekBarHandleStyle">
            <div class="desktop-seek-bar-handle"></div>
          </div>
        </div>

        <div class="desktop-buttons-container" @click.stop="''" @dblclick.stop="''">

          <img class="desktop-prev-button" v-if="skipEnabled && prevEnabled" :class="'large'" alt="Vidéo précédente" src="../../static/icons/videoplayer/skip-prev-128x128-white.png" @click.stop="skipPrev" @dblclick.stop="''"/>
          <img class="desktop-prev-button" v-if="skipEnabled && !prevEnabled" :class="'large'" alt="Vidéo précédente" src="../../static/icons/videoplayer/skip-prev-128x128-gris.png"/>

          <img class="desktop-play-pause-button" v-if="playing && !paused && !ended" :class="'large' + ' ' + (skipEnabled ? '' : 'alone')" alt="Lancer la vidéo" src="../../static/icons/videoplayer/pause-128x128-white.png" @click.stop="pauseVideo" @dblclick.stop="''"/>
          <img class="desktop-play-pause-button" v-if="(!playing && paused && !ended) || loading" :class="'large' + ' ' + (skipEnabled ? '' : 'alone')" alt="Lancer la vidéo" src="../../static/icons/videoplayer/play-128x128-white.png" @click.stop="playVideo" @dblclick.stop="''"/>
          <img class="desktop-play-pause-button" v-if="ended" :class="'large'" alt="Rejouer la vidéo" src="../../static/icons/videoplayer/replay-128x128-white.png" @click.stop="playVideo"/>

          <img class="desktop-next-button" v-if="skipEnabled && nextEnabled" :class="'large'" alt="Vidéo suivante" src="../../static/icons/videoplayer/skip-next-128x128-white.png" @click.stop="skipNext" @dblclick.stop="''"/>
          <img class="desktop-next-button" v-if="skipEnabled && !nextEnabled" :class="'large'" alt="Vidéo suivante" src="../../static/icons/videoplayer/skip-next-128x128-gris.png"/>

          <div class="desktop-volume-container" :class="'large'">
            <img class="desktop-volume-icon" alt="Volume" :class="'large'" :src="volumeIcon" @click.stop="volumeButtonClicked" @dblclick.stop="''"/>
            <div class="desktop-volume-bar-container"
                 :ref="volumeBarRef"
                 @mousedown.stop="volumeBarMouseDown"
                 @click.stop="''"
                 @dblclick.stop="''">
              <div class="desktop-volume-bar-background"></div>
              <div class="desktop-volume-bar-active" :style="activeVolumeBarStyle"></div>
              <div class="desktop-volume-bar-handle-container"
                   :style="volumeBarHandleStyle">
                <div class="desktop-volume-bar-handle" :class="volume === 0 ? 'mute' : ''"></div>
              </div>
            </div>
          </div>

          <div class="desktop-time-container" :class="'large'">
            <p class="desktop-time-txt" :class="'large'"><b style="font-weight: 500;">{{ currentTimeTxt }}</b><b style="font-weight: 400; color: #e8e8e8;"> / {{ durationTxt }}</b></p>
          </div>

          <!-- <img class="desktop-slowmo-button" v-if="!slowmo" alt="Ralenti" :class="'large'" src="../../static/icons/videoplayer/slow-motion-256x82-white.png" @click.stop="slowmoClicked" @dblclick.stop="''"/>
          <img class="desktop-slowmo-button" v-if="slowmo" alt="Ralenti" :class="'large'" src="../../static/icons/videoplayer/slow-motion-256x82-saumon.png" @click.stop="slowmoClicked" @dblclick.stop="''"/> -->

          <div class="desktop-parameters-container" :class="'large'">
            <img class="desktop-parameters-button" alt="Paramètres" :class="'large'" src="../../static/icons/videoplayer/reglages-128x128-white.png" @click.stop="parametersClicked" @dblclick.stop="''"/>
            <div class="desktop-parameters" v-show="!partnerEmbed && (qualityLevels.length !== 0)" :class="parametersToggled ? 'toggled' : 'hidden'">
              <p class="desktop-parameters-title" @click.stop="''">Qualité</p>
              <p class="desktop-parameters-item" :class="qualityChosen === -1 ? 'active' : ''" @click.stop="changeQuality(-1)">Auto</p>
              <p class="desktop-parameters-item" v-for="(quality, index) in qualityLevels" :key="index" :class="qualityChosen === index ? 'active' : ''" @click.stop="changeQuality(index)">{{ quality }}p</p>
            </div>
          </div>

          <img class="desktop-fullscreen-button" v-if="fullscreenAvailable && !fullscreen" :class="'large'" alt="Plein écran" src="../../static/icons/videoplayer/fullscreen-enter-128x128-white.png" @click.stop="fullscreenClicked" @dblclick.stop="''"/>
          <img class="desktop-fullscreen-button" v-if="fullscreenAvailable && fullscreen" :class="'large'" alt="Quitter le plein écran" src="../../static/icons/videoplayer/fullscreen-leave-128x128-white.png" @click.stop="fullscreenClicked" @dblclick.stop="''"/>
        </div>
      </div>

    </div>

  </div>

</template>

<script>

import Hls from 'hls.js'

export default {
  name: 'DesktopVideoPlayer',
  props: ['video', 'playbackRate', 'embed', 'widget', 'partnerEmbed', 'staticVideo', 'noEstat', 'prevEnabled', 'nextEnabled'],
  data () {
    return {
      hls: null,

      mutedAutoplay: false,
      mutedNotAutoplay: false,

      qualityLevels: [],
      qualityChosen: -1,

      parametersToggled: false,
      qualityOptionToggled: false,

      playerHeight: 0,

      posterDisplayed: false,
      posterHovered: false,

      controlsDisplayed: false,
      controlsBlurred: true,

      cursorPosition: null,

      loading: true,
      playing: false,
      paused: false,
      ended: false,

      durationReady: false,

      currentTime: 0,
      duration: 0,

      playAnimToggled: false,
      pauseAnimToggled: false,

      contentStreamTag: null,
      eS: null,

      hitTriggered: false,

      refreshSeekBarInterval: null,
      seekBarClicked: false,
      seekTime: '',
      readSeekBarStyle: 'right: 100%;',
      bufferedSeekBarStyle: 'right: 100%;',
      tooltipStyle: 'left: -1%;',
      seekBarHandleStyle: 'left: -1%;',

      volume: 1,
      volumeBarClicked: false,
      activeVolumeBarStyle: 'right: 100%;',
      volumeBarHandleStyle: 'left: -1%;',
      volumeBeforeMute: 1,

      skeletonDisplayed: true,

      slowmo: false,

      fullscreen: false,
      fullscreenAvailable: true,

      pageClosing: false
    }
  },
  mounted () {
    this.fullscreenAvailable = document.fullscreenEnabled || document.webkitFullscreenEnabled || document.mozFullScreenEnabled || document.msFullscreenEnabled

    this.setPlayerHeight()

    if (this.$route.query.autoplay === '1') {
      this.mutedAutoplay = true
    }
    if (this.$route.query.mute === '1' && !this.$route.query.autoplay) {
      this.mutedNotAutoplay = true
    }
    if (this.embed) {
      window.addEventListener('message', this.messageFromWindow, false)
    }

    this.loadVideo()

    this.updateVolumeBarStyle(this.volume)
    this.updateVolume(this.volume)
  },
  methods: {
    changeQuality (quality) {
      this.loading = true
      this.qualityChosen = quality
      this.hls.currentLevel = quality
      this.parametersToggled = false
      this.qualityOptionToggled = false
      setTimeout(() => {
        if (this.loading) {
          this.loading = false
        }
      }, 500)
    },
    controlsMouseover () {
      this.controlsDisplayed = true
      this.controlsBlurred = false
      if (!this.seekBarClicked) {
        window.addEventListener('mousemove', this.controlsMouseMove, true)
      }
    },
    controlsMouseMove (e) {
      let previouscursorposition = {
        x: e.pageX,
        y: e.pageY
      }
      if (this.cursorPosition && (this.cursorPosition.x !== previouscursorposition.x || this.cursorPosition.y !== previouscursorposition.y)) {
        this.controlsDisplayed = true
      }
      this.cursorPosition = {
        x: e.pageX,
        y: e.pageY,
        targetClass: e.target.className
      }
      setTimeout(() => {
        if (this.cursorPosition.x === previouscursorposition.x && this.cursorPosition.y === previouscursorposition.y) {
          this.controlsDisplayed = false
        } else if (this.controlsBlurred) {
          this.controlsDisplayed = false
        } else {
          this.controlsDisplayed = true
        }
      }, 2000)
    },
    controlsMouseout () {
      window.removeEventListener('mousemove', this.controlsMouseMove, true)
      this.controlsBlurred = true
      if (!this.parametersToggled && !this.qualityOptionToggled) {
        this.controlsDisplayed = false
      }
    },
    durationChange () {
      if (!this.durationReady) {
        this.refreshSeekBarInterval = setInterval(() => {
          this.updateTime()
          this.updateSeekBarStyle()
        }, 10)
        this.durationReady = true
      }
    },
    escapeFullscreen () {
      if (document.mozFullScreen || document.webkitIsFullScreen) {
        this.fullscreen = true
      } else {
        this.fullscreen = false
      }
    },
    flashRewind () {
      this.player.currentTime = this.player.currentTime - 4
      if (this.ended) {
        this.playVideo()
      }
    },
    flashForward () {
      this.player.currentTime = this.player.currentTime + 4
    },
    formatMinutes (n) {
      if (n / 60 >= 1) {
        return Math.floor(n / 60)
      } else {
        return '0'
      }
    },
    formatSeconds (n) {
      return ((n % 60) < 10 ? '0' : '') + (n % 60)
    },
    fullscreenClicked () {
      if (!this.fullscreen) {
        if (this.container.requestFullscreen) {
          this.container.requestFullscreen()
            .then(() => {
              if (window.screen.orientation) {
                window.screen.orientation.lock('landscape-primary')
                  .then(() => {})
                  .catch(() => {})
              } else {
                window.orientation.lock('landscape-primary')
                  .then(() => {})
                  .catch(() => {})
              }
              this.fullscreen = true
              this.container.setAttribute('style', 'width:100%;height:100%;')
            })
        } else if (this.container.mozRequestFullScreen) {
          this.container.mozRequestFullScreen()
            .then(() => {
              if (window.screen.orientation) {
                window.screen.orientation.lock('landscape-primary')
                  .then(() => {})
                  .catch(() => {})
              } else {
                window.orientation.lock('landscape-primary')
                  .then(() => {})
                  .catch(() => {})
              }
              this.fullscreen = true
              this.container.setAttribute('style', 'width:100%;height:100%;')
            })
        } else if (this.container.webkitRequestFullscreen) {
          this.container.webkitRequestFullscreen()
            .then(() => {
              if (window.screen.orientation) {
                window.screen.orientation.lock('landscape-primary')
                  .then(() => {})
                  .catch(() => {})
              } else {
                window.orientation.lock('landscape-primary')
                  .then(() => {})
                  .catch(() => {})
              }
              this.fullscreen = true
              this.container.setAttribute('style', 'width:100%;height:100%;')
            })
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen()
          this.fullscreen = false
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen()
          this.fullscreen = false
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
          this.fullscreen = false
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
          this.fullscreen = false
        }
      }
    },
    getDayMonthYear (datestr) {
      let datum = new Date(datestr)
      return datum.toLocaleString(`${this.$localeRegion}-${this.$country}`, {
        day: 'numeric',
        month: '2-digit',
        year: 'numeric'
      })
    },
    getHour (datestr) {
      let datum = new Date(datestr)
      return datum.toLocaleString(`${this.$localeRegion}-${this.$country}`, {
        hour: '2-digit'
      })
        .substring(0, 2)
    },
    getWeekDay (datestr) {
      let datum = new Date(datestr)
      return datum.toLocaleString(`${this.$localeRegion}-${this.$country}`, {
        weekday: 'long'
      })
    },
    loadVideo () {
      if (Hls.isSupported()) {
        this.hls = new Hls({maxMaxBufferLength: 0})
        this.hls.loadSource(this.video.streamURL)
        this.hls.attachMedia(this.player)
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          for (const level of this.hls.levels) {
            this.qualityLevels.push(level.height)
          }
          if (!this.staticVideo && (!this.embed || this.mutedAutoplay)) {
            if (this.mutedAutoplay) {
              this.updateVolume(0)
              this.updateVolumeBarStyle(0)
            }
            if (this.video.duration && this.video.duration > 400000) {
              this.hls.config.maxMaxBufferLength = this.video.duration * 0.0001
            } else {
              this.hls.config.maxMaxBufferLength = 40
            }
            this.player.play()
              .then(() => {
                this.player.playbackRate = this.playbackRate
                this.posterDisplayed = false
                this.$store.dispatch('hitVideo', this.video.id)
                this.readyForWindowMessages()
                this.skeletonDisplayed = false
                this.setPlayingState()
              })
              .catch(() => {
                this.skeletonDisplayed = false
                if (!this.playing) {
                  this.setPoster()
                }
              })
          } else {
            this.skeletonDisplayed = false
            if (this.mutedNotAutoplay) {
              this.updateVolume(0)
              this.updateVolumeBarStyle(0)
            }
            this.setPoster()
          }
        })
      }
    },
    messageFromWindow (e) {
      if (e.data === 'play') {
        this.playVideo()
      } else if (e.data === 'pause') {
        this.pauseVideo()
      }
    },
    nextMatch () {
      this.$emit('nextMatch')
    },
    nextVideo () {
      this.$emit('nextVideo')
    },
    parametersClicked () {
      this.parametersToggled = !this.parametersToggled
      if (this.qualityOptionToggled) {
        this.qualityOptionToggled = false
      }
    },
    pauseVideo () {
      this.player.pause()
      this.setPausedState()
      this.togglePauseAnim()
    },
    playVideo () {
      if (this.posterDisplayed) {
        this.posterClicked()
        return
      }
      if (this.ended) {
        this.player.currentTime = 0
      }
      this.player.play()
      this.setPlayingState()
      this.togglePlayAnim()
    },
    playerClicked (evt) {
      if (this.parametersToggled) {
        this.parametersClicked()
        return
      }
      if (this.ended && evt !== 'space') {
        return
      }
      if (this.playing) {
        this.pauseVideo()
      } else {
        this.playVideo()
      }
    },
    posterClicked () {
      this.posterDisplayed = false
      if (this.hls) {
        if (this.hls.config.maxMaxBufferLength === 0) {
          if (this.video.duration && this.video.duration > 300000) {
            this.hls.config.maxMaxBufferLength = this.video.duration * 0.0001
          } else {
            this.hls.config.maxMaxBufferLength = 40
          }
        }
      }
      this.playVideo()
    },
    posterMouseover () {
      this.posterHovered = true
    },
    posterMouseout () {
      this.posterHovered = false
    },
    prevMatch () {
      this.$emit('prevMatch')
    },
    prevVideo () {
      this.$emit('prevVideo')
    },
    readyForWindowMessages () {
      if (parent !== window) {
        parent.postMessage('ready', '*')
      }
    },
    resumeAfterBuffering () {
      this.playing = true
      this.paused = false
      this.loading = false
      this.player.removeEventListener('canplay', this.resumeAfterBuffering, true)
    },
    seekBarMouseDown (e) {
      this.seekBarClicked = true
      clearInterval(this.refreshSeekBarInterval)
      let rect = this.seekBar.getBoundingClientRect()
      this.updateSeekBarStyle((e.pageX - rect.left) / this.seekBar.clientWidth * this.duration)
      this.updateSeekTime(((e.pageX - rect.left) / this.seekBar.clientWidth * this.duration))
      window.addEventListener('mousemove', this.seekBarMouseMove, true)
      window.addEventListener('mouseup', this.seekBarMouseUp, true)
    },
    seekBarMouseMove: function (e) {
      if (this.seekBarClicked) {
        let rect = this.seekBar.getBoundingClientRect()
        this.updateSeekBarStyle((e.pageX - rect.left) / this.seekBar.clientWidth * this.duration)
        this.updateSeekTime(((e.pageX - rect.left) / this.seekBar.clientWidth * this.duration))
      }
    },
    seekBarMouseUp: function (e) {
      let rect = this.seekBar.getBoundingClientRect()
      let current = (e.pageX - rect.left) / this.seekBar.clientWidth * this.duration
      if (current < 0) {
        current = 0
      } else if (current > this.duration) {
        current = this.duration
      }
      this.updateSeekBarStyle(current)
      this.updateSeekTime(current)
      this.player.currentTime = current
      window.removeEventListener('mousemove', this.seekBarMouseMove, true)
      window.removeEventListener('mouseup', this.seekBarMouseUp, true)
      this.refreshSeekBarInterval = setInterval(() => {
        this.updateTime()
        this.updateSeekBarStyle()
      }, 10)
      this.seekBarClicked = false
      if (this.ended) {
        this.ended = false
        this.playVideo()
      }
    },
    setPausedState () {
      this.playing = false
      this.paused = true
      this.ended = false

      this.loading = false
    },
    setPlayingState () {
      this.playing = true
      this.paused = false
      this.ended = false

      this.loading = false
    },
    setPlayerHeight () {
      if (this.container) {
        this.playerHeight = `${this.container.offsetWidth * 9 / 16}`
      }
    },
    setPoster () {
      this.posterDisplayed = true
      this.poster.setAttribute('style', `background: url("${this.video.thumbnailURL}");background-size: cover;-webkit-background-size: cover;background-repeat: no-repeat;background-position: center center;`)
      if (this.mutedNotAutoplay) {
        this.readyForWindowMessages()
      }
    },
    skipPrev () {
      this.$emit('skipPrev')
    },
    skipNext () {
      this.$emit('skipNext')
    },
    slowmoClicked () {
      if (this.player.playbackRate === 1) {
        this.player.preservesPitch = false
        this.player.mozPreservesPitch = false
        this.player.webkitPreservesPitch = false
        this.player.playbackRate = 0.5
        this.slowmo = true
      } else {
        this.player.playbackRate = 1
        this.slowmo = false
      }
    },
    startBuffering () {
      if (this.loading) {
        return
      }

      if (this.playing) {
        this.playing = false
        this.paused = false
        this.loading = true
        this.player.addEventListener('canplay', this.resumeAfterBuffering, true)
      }
    },
    toggleAddToCream () {
      this.$emit('toggleAddToCream')
    },
    togglePlayAnim () {
      if (!this.playAnimToggled && !this.pauseAnimToggled) {
        this.playAnimToggled = true
        setTimeout(() => {
          this.playAnimToggled = false
        }, 500)
      }
    },
    togglePauseAnim () {
      if (!this.pauseAnimToggled && !this.playAnimToggled) {
        this.pauseAnimToggled = true
        setTimeout(() => {
          this.pauseAnimToggled = false
        }, 500)
      }
    },
    updateSeekBarStyle (currenttime) {
      if (currenttime) {
        let curr = currenttime
        if (curr < 0) {
          curr = 0
        } else if (curr > this.duration) {
          curr = this.duration
        }
        this.readSeekBarStyle = `right: ${100 - (curr / this.duration * 100)}%;`
        this.seekBarHandleStyle = `left: ${(curr / this.duration * 100) - this.twelvePixels}%;`
        this.tooltipStyle = `left: ${(curr / this.duration * 100) - (this.twelvePixels * 2)}%;`
        if (this.buffered) {
          this.bufferedSeekBarStyle = `right: ${100 - (this.player.buffered.end(this.player.buffered.length - 1) / this.duration * 100)}%;`
        }
      } else {
        this.readSeekBarStyle = `right: ${100 - (this.currentTime / this.duration * 100)}%;`
        this.seekBarHandleStyle = `left: ${(this.currentTime / this.duration * 100) - this.twelvePixels}%;`
        this.tooltipStyle = `left: ${(this.currentTime / this.duration * 100) - (this.twelvePixels * 2)}%;`
        if (this.buffered) {
          let buff = Math.round((100 - (this.player.buffered.end(this.player.buffered.length - 1) / this.duration * 100) + Number.EPSILON) * 100) / 100
          if (buff < 0) {
            buff = 0
          }
          this.bufferedSeekBarStyle = `right: ${buff}%;`
        }
      }
    },
    updateSeekTime (currenttime) {
      let curr = currenttime
      if (curr < 0) {
        curr = 0
      } else if (curr > this.duration) {
        curr = this.duration
      }
      this.seekTime = curr
    },
    updateTime () {
      if (this.duration === 0 && this.player.duration) {
        this.duration = this.player.duration
      }
      if (!this.seekBarClicked) {
        this.currentTime = this.player.currentTime
      }
    },
    updateVolume (value) {
      let val = value
      if (val < 0) {
        val = 0
      } else if (val > 1) {
        val = 1
      }
      this.volume = val
      this.player.volume = val
    },
    updateVolumeBarStyle (currentvolume) {
      if (currentvolume) {
        let curr = currentvolume
        if (curr <= 0) {
          curr = 0
        } else if (curr >= 1) {
          curr = 1
        }
        this.activeVolumeBarStyle = `right: ${100 - (curr * 100)}%;`
        this.volumeBarHandleStyle = `left: ${(curr * 100) - 18}%;`
      } else {
        let curr = this.volume
        if (curr <= 0) {
          curr = 0
        } else if (curr >= 1) {
          curr = 1
        }
        this.activeVolumeBarStyle = `right: ${100 - (curr * 100)}%;`
        this.volumeBarHandleStyle = `left: ${(curr * 100) - 18}%;`
      }
    },
    videoEnded () {
      this.player.pause()
      this.playing = false
      this.paused = false
      this.ended = true
      this.loading = false
    },
    volumeBarMouseDown (e) {
      this.volumeBarClicked = true
      let rect = this.volumeBar.getBoundingClientRect()
      this.updateVolumeBarStyle((e.pageX - rect.left) / this.volumeBar.clientWidth)
      this.updateVolume(((e.pageX - rect.left) / this.volumeBar.clientWidth))
      window.addEventListener('mousemove', this.volumeBarMouseMove, true)
      window.addEventListener('mouseup', this.volumeBarMouseUp, true)
    },
    volumeBarMouseMove: function (e) {
      if (this.volumeBarClicked) {
        let rect = this.volumeBar.getBoundingClientRect()
        let current = (e.pageX - rect.left) / this.volumeBar.clientWidth
        if (current <= 0) {
          current = 0
        } else if (current >= 1) {
          current = 1
        }
        this.updateVolumeBarStyle(current)
        this.updateVolume(current)
      }
    },
    volumeBarMouseUp: function (e) {
      let rect = this.volumeBar.getBoundingClientRect()
      let current = (e.pageX - rect.left) / this.volumeBar.clientWidth
      if (current <= 0) {
        current = 0
      } else if (current >= 1) {
        current = 1
      }
      this.updateVolumeBarStyle(current)
      this.updateVolume(current)
      window.removeEventListener('mousemove', this.volumeBarMouseMove, true)
      window.removeEventListener('mouseup', this.volumeBarMouseUp, true)
      this.volumeBarClicked = false
    },
    volumeButtonClicked () {
      if (this.volume === 0) {
        this.updateVolume(this.volumeBeforeMute)
        this.updateVolumeBarStyle(this.volumeBeforeMute)
      } else {
        this.volumeBeforeMute = this.volume
        this.updateVolume(0)
        this.updateVolumeBarStyle(0)
      }
    }
  },
  computed: {
    buffered () {
      if (this.player) {
        return this.player.buffered
      }
    },
    currentTimeTxt () {
      return `${this.formatMinutes(Math.floor(this.currentTime))}:${this.formatSeconds(Math.floor(this.currentTime))}`
    },
    durationTxt () {
      return `${this.formatMinutes(Math.floor(this.duration))}:${this.formatSeconds(Math.floor(this.duration))}`
    },
    playerStyle () {
      if (!this.fullscreen) {
        return `height: ${this.playerHeight}px;`
      }
    },
    embedToRematchLink () {
      if (this.widget) {
        return `https://www.rematch.tv/match/${this.video.matchID}?videoId=${this.video.id}`
      }
      if (this.video.type === 'summary') {
        return `https://www.rematch.tv/match/${this.video.matchID}`
      } else {
        return `https://www.rematch.tv/video/${this.video.id}`
      }
    },
    skipEnabled () {
      return this.prevEnabled || this.nextEnabled
    },
    twelvePixels () {
      return ((12 / window.innerWidth) * 100)
    },
    volumeIcon () {
      if (this.volume === 0) {
        return '../../static/icons/videoplayer/volume-mute-128x128-white.png'
      } else if (this.volume < 0.3) {
        return '../../static/icons/videoplayer/volume-low-128x128-white.png'
      } else if (this.volume < 0.8) {
        return '../../static/icons/videoplayer/volume-medium-128x128-white.png'
      } else {
        return '../../static/icons/videoplayer/volume-high-128x128-white.png'
      }
    },
    containerRef () {
      return `container-${this.video.id}`
    },
    container () {
      return this.$refs[this.containerRef]
    },
    playerRef () {
      return `player-${this.video.id}`
    },
    player () {
      return this.$refs[this.playerRef]
    },
    posterRef () {
      return `poster-${this.video.id}`
    },
    poster () {
      return this.$refs[this.posterRef]
    },
    volumeBarRef () {
      return `volumebar-${this.video.id}`
    },
    volumeBar () {
      return this.$refs[this.volumeBarRef]
    },
    seekBarRef () {
      return `seekbar-${this.video.id}`
    },
    seekBar () {
      return this.$refs[this.seekBarRef]
    }
  },
  watch: {
    'currentTime' () {
      this.updateSeekBarStyle()
      if (this.currentTime >= this.duration) {
        this.videoEnded()
      }
    },
    'playbackRate' () {
      this.player.playbackRate = this.playbackRate
    }
  },
  beforeDestroy () {
    this.pageClosing = true
    clearInterval(this.refreshSeekBarInterval)
    if (this.hls) {
      this.hls.stopLoad()
      this.hls.destroy()
    } else {
      this.player.pause()
      this.player.removeAttribute('src')
      this.player.load()
    }
  }
}

</script>

<style lang="scss" scoped>

  .desktop-video-player-container {
    position: relative;
    height: fit-content;
    background-color: black;
    display: grid;
  }

  .desktop-video-player {
    background-color: black;
    height: 100%;
    &:focus {
      outline: none;
    }
  }

  .desktop-video-player-poster {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;

    cursor: pointer;
    display: grid;
  }

  .desktop-club-names-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    padding: 18px 18px 60px 18px;
    background: linear-gradient(to bottom, #000019, rgba(#000019, 0.6), transparent);
    &.in-player {
      grid-area: header;
      align-self: start;
    }
  }

  .desktop-club-names {
    font-weight: 500;
    font-size: 18px;
    padding-right: 12px;
    width: fit-content;
    height: fit-content;
    color: white;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  .big-play-button {
    align-self: center;
    justify-self: center;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    &.small, &.medium {
      width: 60px;
    }
    &.large, &.xlarge {
      width: 72px;
    }
  }

  .desktop-skeleton-container {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: grid;
  }

  .desktop-skeleton-loader {
    width: 44px;
    height: 44px;
    align-self: center;
    justify-self: center;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .desktop-anim-container {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: grid;
  }

  .desktop-anim-content {
    width: 72px;
    height: 72px;
    align-self: center;
    justify-self: center;
  }

  .desktop-play-anim, .desktop-pause-anim {
    width: 72px;
    height: 72px;
    opacity: 0;
  }

  .desktop-buffering-loader-container {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: grid;
  }

  .desktop-buffering-loader {
    width: 44px;
    height: 44px;
    align-self: center;
    justify-self: center;
  }

  .desktop-ended-overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    width: 100%;
    height: 100%;
    background-color: #000019;
    opacity: .92;
    display: grid;
  }

  .desktop-ended-replay-button {
    align-self: center;
  }

  .desktop-video-player-controls {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    display: grid;
    grid-template-rows: 1fr auto 1fr;
    grid-template-areas:
      "header"
      "middle"
      "bar";

    -webkit-transition: opacity .25s;
    -moz-transition: opacity .25s;
    -o-transition: opacity .25s;
    -ms-transition: opacity .25s;
    transition: opacity .25s;
    transition-timing-function: ease;
    &.visible {
      cursor: auto;
      opacity: 1;
    }
    &.hidden {
      cursor: none;
    }
  }

  .desktop-controls-bar {
    grid-area: bar;
    align-self: end;
    height: 62px;
    padding: 6px;
    background: linear-gradient(to top, rgba(#000019, 0.84), rgba(#000019, 0.48), transparent);
    display: grid;
    grid-template-rows: 12px 1fr;
  }

  .desktop-seek-bar {
    cursor: pointer;
    height: 12px;
    width: 98.5%;
    justify-self: center;
    position: relative;
    &:hover .desktop-seek-bar-pending {
      top: 6px;
      height: 5.5px;
    }
    &:hover .desktop-seek-bar-buffered {
      top: 6px;
      height: 5.5px;
    }
    &:hover .desktop-seek-bar-read {
      top: 6px;
      height: 5.5px;
    }
    &:hover .desktop-seek-bar-handle-container {
      top: -1px;
      width: 19px;
      height: 19px;
    }
    &:hover .desktop-seek-bar-handle {
      width: 15px;
      height: 15px;
    }
  }

  .desktop-seek-bar-pending {
    position: absolute;
    top: 7px;
    right: 0;
    bottom: 0;
    left: 0;
    height: 4px;
    background-color: grey;
    border-radius: 2px;
  }

  .desktop-seek-bar-buffered {
    position: absolute;
    top: 7px;
    bottom: 0;
    left: 0;
    height: 4px;
    background-color: lightgrey;
    border-radius: 2px;
  }

  .desktop-seek-bar-read {
    position: absolute;
    top: 7px;
    bottom: 0;
    left: 0;
    height: 4px;
    background-color: orangered;
    border-radius: 2px;
  }

  .desktop-seek-bar-tooltip {
    position: absolute;
    width: 32px;
    height: 18px;
    background-color: orangered;
    top: -20px;
    border-radius: 2px;
    display: grid;
  }

  .desktop-seek-bar-tooltip-txt {
    font-size: 12px;
    font-weight: 500;
    align-self: center;
    text-align: center;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .desktop-seek-bar-handle-container {
    pointer-events: none;
    position: absolute;
    width: 16px;
    height: 16px;
    padding: 2px;
    top: 1px;
  }

  .desktop-seek-bar-handle {
    pointer-events: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: orangered;
    box-shadow: 2px 2px 6px rgba(darkslategrey, 0.72);
  }

  .desktop-buttons-container {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    display: grid;
    grid-template-columns: auto auto auto auto auto 1fr auto auto auto;
    grid-template-areas:
      "prev play next volume time . slowmo parameters fullscreen";
  }

  .desktop-prev-button {
    grid-area: prev;
    cursor: pointer;
    height: 42px;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    &.small, &.medium {
      padding: 11px;
    }
    &.large, &.xlarge {
      padding: 11px 14px;
    }
  }

  .desktop-play-pause-button {
    grid-area: play;
    cursor: pointer;
    height: 42px;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    &.small, &.medium {
      padding: 10px 11px;
      &.alone {
        padding: 10px 11px 10px 23px;
      }
    }
    &.large, &.xlarge {
      padding: 10px 14px;
      &.alone {
        padding: 10px 14px 10px 26px;
      }
    }
  }

  .desktop-next-button {
    grid-area: next;
    cursor: pointer;
    height: 42px;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    &.small, &.medium {
      padding: 11px;
    }
    &.large, &.xlarge {
      padding: 11px 14px;
    }
  }

  .desktop-volume-container {
    grid-area: volume;
    padding-right: 11px;
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr;
    &.small, &.medium {
      padding-right: 11px;
    }
    &.large, &.xlarge {
      padding-right: 14px;
    }
  }

  .desktop-volume-icon {
    cursor: pointer;
    height: 42px;
    align-self: center;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    &.small, &.medium {
      padding: 11px;
    }
    &.large, &.xlarge {
      padding: 11px 14px;
    }
  }

  .desktop-volume-bar-container {
    cursor: pointer;
    position: relative;
    width: 60px;
    height: 40%;
    align-self: center;
  }

  .desktop-volume-bar-background {
    position: absolute;
    top: 6px;
    right: 0;
    bottom: 0;
    left: 0;
    height: 4px;
    background-color: grey;
    border-radius: 2px;
  }

  .desktop-volume-bar-active {
    position: absolute;
    top: 6px;
    bottom: 0;
    left: 0;
    height: 4px;
    background-color: white;
    border-radius: 2px;
  }

  .desktop-volume-bar-handle-container {
    pointer-events: none;
    position: absolute;
    width: 21px;
    height: 21px;
    padding: 3px;
    top: -1px;
  }

  .desktop-volume-bar-handle {
    pointer-events: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: white;
    box-shadow: 2px 2px 6px rgba(darkslategrey, 0.72);
    &.mute {
      background-color: darkslategrey;
    }
  }

  .desktop-time-container {
    grid-area: time;
    align-self: center;
    height: fit-content;
    &.small, &.medium {
      padding: 0 11px 1px 11px;
    }
    &.large, &.xlarge {
      padding: 0 14px 1px 14px;
    }
  }

  .desktop-time-txt {
    color: lightgrey;
    margin-bottom: 0;
    &.small, &.medium {
      font-size: 12px;
    }
    &.large, &.xlarge {
      font-size: 13px;
    }
  }

  .desktop-slowmo-button {
    grid-area: slowmo;
    cursor: pointer;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    height: 42px;
    align-self: center;
    &.small, &.medium {
      padding: 14px 11px;
    }
    &.large, &.xlarge {
      padding: 14px;
    }
  }

  .desktop-parameters-container {
    grid-area: parameters;
    position: relative;
    cursor: pointer;
    height: 42px;
    &.small, &.medium {
      padding: 11px;
    }
    &.large, &.xlarge {
      padding: 11px 14px;
    }
  }

  .desktop-parameters-button {
    height: 100%;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .desktop-parameters {
    position: absolute;
    top: -192px;
    right: -42px;
    opacity: 0;
    width: 0;
    height: auto;
    z-index: 2;
    overflow: hidden;
    cursor: auto;
    border: 1px solid lightgrey;
    background-color: white;
    box-shadow: 2px 2px 6px rgba(darkslategrey, 0.72);
    -webkit-transition: width .25s;
    -moz-transition: width .25s;
    -o-transition: width .25s;
    -ms-transition: width .25s;
    transition: width .25s;
    transition-timing-function: ease;
    &.toggled {
      opacity: 1;
      width: 144px;
      height: auto;
    }
    &.options-container {
      top: -88px;
    }
  }

  .desktop-parameters-title {
    padding: 8px 12px;
    color: black;
    font-weight: 500;
    border-bottom: 1px solid lightgrey;
  }

  .desktop-parameters-item {
    padding: 10px 12px;
    color: darkslategrey;
    cursor: pointer;
    &:hover {
      background-color: lightgrey;
    }
    &.active {
      background-color: darkblue;
      color: white;
      font-weight: 700;
    }
  }

  .desktop-parameters-option {
    cursor: pointer;
    text-decoration: none;
    padding: 10px 6px;
    display: grid;
    grid-template-columns: 1fr auto;
    grid-column-gap: 6px;
    &:hover {
      background-color: lightgrey;
    }
  }

  .desktop-parameters-option-txt {
    color: darkblue;
  }

  .desktop-parameters-option-img {
    height: 18px;
  }

  .desktop-fullscreen-button {
    grid-area: fullscreen;
    cursor: pointer;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    height: 42px;
    &.small, &.medium {
      padding: 11px;
    }
    &.large, &.xlarge {
      padding: 11px 14px;
    }
  }

  .playanim-enter-active, .playanim-leave-active, .pauseanim-enter-active, .pauseanim-leave-active {
    transition: all .5s;
    overflow: hidden;
  }

  .playanim-enter, .pauseanim-enter {
    padding: 8px;
    opacity: 0.7;
  }

  .playanim-enter-to, .pauseanim-enter-to {
    padding: 0;
    opacity: 0;
  }

  .endedoverlay-enter-active, .endedoverlay-leave-active {
    transition: all .2s;
  }

  .endedoverlay-enter, .endedoverlay-leave-to {
    opacity: 0;
  }

  .endedoverlay-leave, .endedoverlay-enter-to {
    opacity: .8;
  }

</style>



// WEBPACK FOOTER //
// src/components/DesktopVideoPlayer.vue